import { StyleConfig } from '@chakra-ui/react';

// Marketplace dashboard secondary buttons. blueOutline is an alternative the driver can act on;
// quietOutline is a standing reminder that must not compete with nearby offer actions.
const outlineBase = {
  bg: 'white',
  color: 'leaseEndBlue',
  borderWidth: '1px',
  fontWeight: '600',
  _hover: { bg: 'azureishWhite', textDecoration: 'none' },
  _disabled: { bg: 'gainsboro', color: 'oldSilver', opacity: '1' },
};

const button: StyleConfig = {
  baseStyle: {
    borderRadius: '0.3rem',
    fontWeight: 'bold',
    fontFamily: 'body',
    whiteSpace: 'break-spaces',
    textAlign: 'center',
  },
  sizes: {
    sm: { py: '10px', px: '20px', fontSize: '14px', lineHeight: '16px' },
    md: { py: '25px', px: '40px', fontSize: '14px', lineHeight: '16px' },
    lg: { py: '30px', px: '50px', fontSize: '16px', lineHeight: '20px' },
  },
  variants: {
    dashed: {
      h: 'auto',
      py: '12px',
      px: '12px',
      bg: 'white',
      borderWidth: '1px',
      borderStyle: 'dashed',
      borderColor: 'gainsboro',
      borderRadius: '11px',
      color: 'oceanBoatBlue',
      fontSize: '13px',
      fontWeight: '700',
      letterSpacing: '1px',
      _hover: { bg: 'white', boxShadow: 'none' },
      _loading: { bg: 'white', opacity: 0.6, _hover: { bg: 'white' } },
      _disabled: { opacity: 0.6, cursor: 'not-allowed' },
    },
    link: {
      fontSize: '14px',
      color: 'royalBlue',
      fontWeight: 'normal',
    },
    primary: {
      m: 0,
      bg: 'leaseEndYellow',
      color: 'grayDark',
      letterSpacing: '1px',
      _loading: {
        color: 'grayDark',
        opacity: '1',
        bg: 'yellowLight',
        _hover: {
          bg: 'yellowLight',
        },
      },
      _disabled: {
        color: 'oldSilver',
        bg: 'gainsboro',
        opacity: '1',
      },
      _hover: {
        bg: 'yellowLight',
        boxShadow: 'md',
        _disabled: {
          bg: 'gainsboro',
          boxShadow: 'none',
        },
      },
    },
    secondary: {
      m: 0,
      bg: 'white',
      color: 'royalBlue',
      fontWeight: 'bold',
      letterSpacing: '1px',
      border: '1px solid',
      textTransform: 'uppercase',
      _loading: {
        opacity: '0.7',
        bg: 'white',
        color: 'royalBlue',
        borderColor: 'oceanBoatBlue',
        _hover: {
          bg: 'white',
          color: 'royalBlue',
          borderColor: 'oceanBoatBlue',
        },
      },
      _disabled: {
        opacity: '1',
        color: 'taupeGray',
        borderColor: 'taupeGray',
      },
      _hover: {
        color: 'oceanBoatBlue',
        bg: 'azureishWhite',
        boxShadow: '0px 3px 6px #00000029',
        borderColor: 'oceanBoatBlue',
        textDecoration: 'none',
        _disabled: {
          bg: 'white',
          color: 'taupeGray',
          borderColor: 'taupeGray',
          boxShadow: 'none',
        },
      },
    },
    secondaryFilled: {
      m: 0,
      bg: 'royalBlue',
      color: 'white',
      fontWeight: 'bold',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      _loading: {
        opacity: '0.7',
        bg: 'royalBlue',
        color: 'white',
        _hover: {
          bg: 'royalBlue',
          color: 'white',
        },
      },
      _disabled: {
        opacity: '1',
        color: 'taupeGray',
        bg: 'gainsboro',
      },
      _hover: {
        bg: 'oceanBoatBlue',
        boxShadow: 'md',
        _disabled: {
          bg: 'gainsboro',
          boxShadow: 'none',
        },
      },
    },
    danger: {
      bg: 'leaseEndRed',
      color: 'white',
      border: '1px solid transparent',
      _hover: {
        bg: 'redReplace',
        _disabled: { bg: 'grayMediumBackground' },
      },
      _disabled: {
        bg: 'grayMediumBackground',
        color: 'grayMedium',
        opacity: '1',
      },
    },
    dangerOutline: {
      bg: 'transparent',
      border: '1px solid',
      borderColor: 'leaseEndRed',
      color: 'leaseEndRed',
      _hover: { bg: 'leaseEndRedBG' },
    },
    bluePill: {
      bg: 'leaseEndBlue',
      color: 'white',
      border: '1px solid transparent',
      borderRadius: 'full',
      _hover: { bg: 'leaseEndBlueDark' },
    },
    blueOutline: { ...outlineBase, borderColor: 'leaseEndBlue' },
    quietOutline: { ...outlineBase, borderColor: 'gainsboro' },
    secondaryLink: {
      padding: '25px 40px',
      m: 0,
      bg: 'transparent',
      color: 'royalBlue',
      letterSpacing: '1px',
      _hover: {
        color: 'royalBlueLight',
        textDecoration: 'underline',
      },
    },
  },
  defaultProps: {
    variant: 'primary',
  },
};

export default button;
